import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Trash2, Hash, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { insertTagSchema, type Tag } from "@shared/schema";
// 
interface TagWithUsage extends Tag {
  usageCount: number;
}

const TAG_COLORS = [
  { label: "Blue", value: "#3b82f6" },
  { label: "Green", value: "#10b981" },
  { label: "Yellow", value: "#f59e0b" },
  { label: "Red", value: "#ef4444" },
  { label: "Purple", value: "#8b5cf6" },
  { label: "Pink", value: "#ec4899" },
  { label: "Gray", value: "#6b7280" },
  { label: "Teal", value: "#14b8a6" },
  { label: "Indigo", value: "#6366f1" },
  { label: "Orange", value: "#f97316" },
];

const TAG_CATEGORIES = ["Work", "Personal", "Research", "Ideas", "Projects", "Learning"];

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  color: z.string().optional(),
  description: z.string().optional(),
  category: z.string().optional(),
});

export default function TagControlPanel() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const tagId = params.id;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      color: "",
      description: "",
      category: "",
    },
  });

  const { data: tag, isLoading: tagLoading } = useQuery<TagWithUsage>({
    queryKey: ["/api/tags", tagId],
    enabled: !!tagId,
  });

  useEffect(() => {
    if (tag) {
      form.reset({
        name: tag.name,
        color: tag.color || "",
        description: tag.description || "",
        category: tag.category || "",
      });
      setHasUnsavedChanges(false);
    }
  }, [tag, form]);

  useEffect(() => {
    let isInitialLoad = true;
    const subscription = form.watch(() => {
      if (!isInitialLoad) {
        setHasUnsavedChanges(true);
      }
    });
    const timeout = setTimeout(() => {
      isInitialLoad = false;
    }, 100);
    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [form]);

  const updateMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const res = await apiRequest("PATCH", `/api/tags/${tagId}`, data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      setHasUnsavedChanges(false);
      toast({ title: "Tag saved successfully" });
    },
    onError: () => {
      toast({ title: "Failed to save tag", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/tags/${tagId}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      toast({ title: "Tag deleted successfully" });
      setLocation("/tags");
    },
    onError: () => {
      toast({ title: "Failed to delete tag", variant: "destructive" });
    },
  });

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    updateMutation.mutate(data);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (confirm("You have unsaved changes. Are you sure you want to leave?")) {
        setLocation("/tags");
      }
    } else {
      setLocation("/tags");
    }
  };

  const selectedColor = form.watch("color");

  if (tagLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading tag...</div>
      </div>
    );
  }

  if (!tag) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">Tag not found</div>
        <Button variant="outline" onClick={() => setLocation("/tags")}>
          Back to Tags
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleBack}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Hash 
              className="h-6 w-6" 
              style={{ color: selectedColor || "currentColor" }}
            />
            <h1 className="text-2xl font-bold" data-testid="text-tag-title">
              Edit Tag
            </h1>
          </div>
        </div>
        <Button 
          onClick={form.handleSubmit(handleSubmit)}
          disabled={updateMutation.isPending}
          data-testid="button-save-tag"
        >
          <Save className="h-4 w-4 mr-2" />
          {updateMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5" />
                Identity
              </CardTitle>
              <CardDescription>
                Basic information about this tag
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter tag name" 
                        {...field} 
                        data-testid="input-tag-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color</FormLabel>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {TAG_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => form.setValue("color", color.value)}
                            className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                              field.value === color.value 
                                ? "border-foreground ring-2 ring-offset-2 ring-foreground" 
                                : "border-transparent"
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.label}
                            data-testid={`color-swatch-${color.label.toLowerCase()}`}
                          />
                        ))}
                        <button
                          type="button"
                          onClick={() => form.setValue("color", "")}
                          className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 flex items-center justify-center bg-muted ${
                            !field.value 
                              ? "border-foreground ring-2 ring-offset-2 ring-foreground" 
                              : "border-border"
                          }`}
                          title="No color"
                          data-testid="color-swatch-none"
                        >
                          <span className="text-xs text-muted-foreground">—</span>
                        </button>
                      </div>
                      {field.value && (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded" 
                            style={{ backgroundColor: field.value }}
                          />
                          <span className="text-sm text-muted-foreground">
                            {TAG_COLORS.find(c => c.value === field.value)?.label || field.value}
                          </span>
                        </div>
                      )}
                    </div>
                    <FormDescription>
                      Choose a color to visually distinguish this tag
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Optional description for this tag" 
                        className="resize-none"
                        rows={3}
                        {...field} 
                        data-testid="textarea-tag-description"
                      />
                    </FormControl>
                    <FormDescription>
                      A brief description of what this tag represents
                    </FormDescription>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Organization
              </CardTitle>
              <CardDescription>
                How this tag is categorized
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select 
                      value={field.value || "none"} 
                      onValueChange={(value) => field.onChange(value === "none" ? "" : value)}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-tag-category">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {TAG_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Group related tags together under a category
                    </FormDescription>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {tag.usageCount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Usage</CardTitle>
                <CardDescription>
                  This tag is currently in use
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {tag.usageCount} {tag.usageCount === 1 ? "item" : "items"}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Changes to this tag will be reflected everywhere it's used
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Irreversible actions for this tag
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="button-delete-tag">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Tag
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Tag</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{tag.name}"?
                      {tag.usageCount > 0 && (
                        <span className="block mt-2 text-destructive font-medium">
                          This tag is currently used in {tag.usageCount}{" "}
                          {tag.usageCount === 1 ? "item" : "items"}. It will be removed
                          from all of them.
                        </span>
                      )}
                      <span className="block mt-2">
                        This action cannot be undone.
                      </span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteMutation.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      data-testid="button-confirm-delete-tag"
                    >
                      Delete Tag
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </form>
      </Form>
    </div>
  );
}
